import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import type { GridPaginationModel } from '@mui/x-data-grid';
import {
  type AuditLogEntry,
  type AuditLogFilters,
  getAuditLogs,
} from '@core/api/auditLog';
import { renderAuditLogSummary } from '@core/audit';
import {
  AuditLogChanges,
  DataTableV2,
  type DataTableV2Column,
  Page,
} from '@core/components';
import { useAuth } from '@core/contexts/AuthContext';
import { ACTOR_TYPE_LABELS, ActorType } from '@core/enum/actorType';
import {
  AUDIT_ACTION_FILTER_OPTIONS,
  AuditAction,
} from '@core/enum/auditAction';
import { UserRole, isAtLeast } from '@core/enum/userRole';
import { navItems } from '../navigation';

const AuditLog = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [filters, setFilters] = useState<AuditLogFilters>({});

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAuditLogs({
        ...filters,
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      });
      setItems(response.items);
      setTotal(response.total);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [filters, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const updateFilter = <K extends keyof AuditLogFilters>(
    key: K,
    value: AuditLogFilters[K] | undefined
  ) => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setFilters((prev) => {
      const next = { ...prev };
      if (value == null || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const clearFilters = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setFilters({});
  };

  const getRowDetails = useCallback(
    (row: AuditLogEntry) => <AuditLogChanges entry={row} />,
    []
  );

  const columns = useMemo<DataTableV2Column<AuditLogEntry>[]>(
    () => [
      {
        field: 'createdAt',
        headerName: 'Timestamp',
        width: 180,
        valueGetter: (_, row) => new Date(row.createdAt).toLocaleString(),
      },
      {
        field: 'actor',
        headerName: 'Actor',
        width: 180,
        valueGetter: (_, row) =>
          row.actorType === ActorType.User
            ? row.actor
              ? `${row.actor.firstName} ${row.actor.lastName}`.trim()
              : 'Unknown'
            : ACTOR_TYPE_LABELS[row.actorType],
      },
      {
        field: 'summary',
        headerName: 'Summary',
        flex: 1,
        minWidth: 240,
        sortable: false,
        renderCell: ({ row }) => renderAuditLogSummary(row),
      },
    ],
    []
  );

  if (!user || !isAtLeast(user.role, UserRole.SuperAdmin)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Page navItems={navItems} path="/logs">
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: 2,
          mb: 2,
          flexShrink: 0,
          pt: 1,
        }}
      >
        <TextField
          select
          size="small"
          label="Action"
          value={filters.action ?? ''}
          onChange={(e) =>
            updateFilter('action', e.target.value as AuditAction)
          }
          sx={{ minWidth: 220 }}
        >
          {AUDIT_ACTION_FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Subject type"
          value={filters.subjectType ?? ''}
          onChange={(e) =>
            updateFilter('subjectType', e.target.value || undefined)
          }
          placeholder="e.g. Order"
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          label="From date"
          type="date"
          value={filters.dateFrom ?? ''}
          onChange={(e) =>
            updateFilter('dateFrom', e.target.value || undefined)
          }
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180, flexShrink: 0 }}
        />
        <TextField
          size="small"
          label="To date"
          type="date"
          value={filters.dateTo ?? ''}
          onChange={(e) => updateFilter('dateTo', e.target.value || undefined)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180, flexShrink: 0 }}
        />
        <Button variant="outlined" onClick={clearFilters}>
          Clear
        </Button>
      </Box>

      <Box
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <DataTableV2<AuditLogEntry>
          rows={items}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          getRowDetails={getRowDetails}
          fillHeight
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Page>
  );
};

export default AuditLog;
